import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import { NgxSliderModule, Options } from '@angular-slider/ngx-slider';
import {
  AviaSearchAppliedFilter,
  AviaSearchAppliedFilterGroup,
  AviaSearchFilterItem,
  AviaSearchFilterPrice,
  AviaSearchFilterSelected,
  AviaSearchFilters,
  AviaSearchFilterValue,
  AviaSearchPriceSelected,
  AviaSearchTimeSelected
} from '../../../../services/router/interfaces/avia-search';

interface AviaFilterOption {
  filterValue: string;
  viewValue: string;
  isNoop: boolean;
}

@Component({
  selector: 'app-filter-sheet',
  templateUrl: './filter-sheet.component.html',
  styleUrls: ['./filter-sheet.component.scss', '../../../../../../../../build/angular/avia-filter-sheet.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgTemplateOutlet, NgxSliderModule
  ],
  standalone: true
})
export class FilterSheetComponent {
  private readonly stopoverFilterKeys = [
    'stops',
    'stopoverDuration',
    'withoutNightStopovers',
  ];
  private readonly departureTimeFilterKeys = [
    'departureTimeFromOrigin',
    'departureTimeFromDestination',
  ];
  private readonly allFilterKeys = [
    'price',
    'airlines',
    'singleAirline',
    'excludeLcc',
    'flightType',
    'stops',
    'stopoverDuration',
    'withoutNightStopovers',
    'baggageOnly',
    'departureTimeFromOrigin',
    'departureTimeFromDestination',
  ];
  private currentFilter: AviaSearchFilterItem | null = null;
  private currentAppliedFilter: AviaSearchAppliedFilter | null = null;
  private currentAppliedFilters: AviaSearchAppliedFilter[] = [];
  private currentFilters: AviaSearchFilters = [];
  private currentFilterGroup: string | null = null;
  private isPriceManuallyChanged = false;
  private isPriceInitiallyApplied = false;

  @Input() set filter(filter: AviaSearchFilterItem | null) {
    this.currentFilter = filter;
    this.initializeCurrentFilter();
  }

  get filter(): AviaSearchFilterItem | null {
    return this.currentFilter;
  }

  @Input() set appliedFilter(appliedFilter: AviaSearchAppliedFilter | null) {
    this.currentAppliedFilter = appliedFilter;
    this.initializeCurrentFilter();
  }

  get appliedFilter(): AviaSearchAppliedFilter | null {
    return this.currentAppliedFilter;
  }

  @Input() set filters(filters: AviaSearchFilters) {
    this.currentFilters = filters || [];

    if (this.currentFilter?.filterKey) {
      this.currentFilter = this.getFilterByKey(this.currentFilter.filterKey) || this.currentFilter;
    }
  }

  get filters(): AviaSearchFilters {
    return this.currentFilters;
  }

  @Input() set appliedFilters(appliedFilters: AviaSearchAppliedFilter[]) {
    this.currentAppliedFilters = appliedFilters || [];
    this.initializeCurrentFilter();
  }

  get appliedFilters(): AviaSearchAppliedFilter[] {
    return this.currentAppliedFilters;
  }

  @Input() set filterGroup(filterGroup: string | null) {
    this.currentFilterGroup = filterGroup;
    this.initializeCurrentFilter();
  }

  get filterGroup(): string | null {
    return this.currentFilterGroup;
  }

  @Output() applyFilter = new EventEmitter<AviaSearchAppliedFilter>();
  @Output() applyFilterGroup = new EventEmitter<AviaSearchAppliedFilterGroup>();
  @Output() resetFilter = new EventEmitter<string>();
  @Output() resetFilterGroup = new EventEmitter<string[]>();

  minPriceLimit = 0;
  maxPriceLimit = 0;
  minPriceValue = 0;
  maxPriceValue = 0;
  minPriceInputValue = '';
  maxPriceInputValue = '';
  priceCurrencySign = '';
  selectedAirlines: string[] = [];
  selectedFlightType = '';
  selectedStops = '';
  selectedStopoverDuration = '';
  selectedWithoutNightStopovers = '';
  singleAirlineOnly = false;
  excludeLcc = false;
  selectedBaggageOnly = '';
  selectedSortBy = 'price';
  departureTimeFromOriginMin = 0;
  departureTimeFromOriginMax = 1439;
  departureTimeFromOriginValue = 0;
  departureTimeFromOriginHighValue = 1439;
  departureTimeFromDestinationMin = 0;
  departureTimeFromDestinationMax = 1439;
  departureTimeFromDestinationValue = 0;
  departureTimeFromDestinationHighValue = 1439;
  noLogoAirlines = new Set<string>();

  priceOptions: Options = {
    floor: 0,
    ceil: 0,
    step: 1,
    translate: (): string => '',
  };
  departureTimeFromOriginOptions: Options = this.createTimeOptions();
  departureTimeFromDestinationOptions: Options = this.createTimeOptions();

  get filterTitle(): string {
    if (this.isAllFiltersGroupFilter) {
      return 'Все фильтры';
    }

    if (this.isStopoversGroupFilter) {
      return 'Пересадки';
    }

    if (this.isDepartureTimeGroupFilter) {
      return 'Время вылета';
    }

    return this.filter?.name || 'Фильтры';
  }

  get isPriceFilter(): boolean {
    return this.filter?.filterKey === 'price';
  }

  get isAirlinesFilter(): boolean {
    return this.filter?.filterKey === 'airlines';
  }

  get isFlightTypeFilter(): boolean {
    return this.filter?.filterKey === 'flightType';
  }

  get isStopoversGroupFilter(): boolean {
    return this.filterGroup === 'stopovers';
  }

  get isAllFiltersGroupFilter(): boolean {
    return this.filterGroup === 'allFilters';
  }

  get excludeLccFilter(): AviaSearchFilterItem | null {
    return this.getFilterByKey('excludeLcc');
  }

  get singleAirlineFilter(): AviaSearchFilterItem | null {
    return this.getFilterByKey('singleAirline');
  }

  get isBaggageOnlyFilter(): boolean {
    return this.filter?.filterKey === 'baggageOnly';
  }

  get isDepartureTimeGroupFilter(): boolean {
    return this.filterGroup === 'departureTime';
  }

  get isSortByFilter(): boolean {
    return this.filter?.filterKey === 'sortBy';
  }

  get priceFilter(): AviaSearchFilterItem | null {
    return this.isPriceFilter
      ? this.filter
      : this.getFilterByKey('price');
  }

  get airlinesFilter(): AviaSearchFilterItem | null {
    return this.isAirlinesFilter
      ? this.filter
      : this.getFilterByKey('airlines');
  }

  get flightTypeFilter(): AviaSearchFilterItem | null {
    return this.isFlightTypeFilter
      ? this.filter
      : this.getFilterByKey('flightType');
  }

  get baggageOnlyFilter(): AviaSearchFilterItem | null {
    return this.isBaggageOnlyFilter
      ? this.filter
      : this.getFilterByKey('baggageOnly');
  }

  get sortByFilter(): AviaSearchFilterItem | null {
    return this.isSortByFilter
      ? this.filter
      : this.getFilterByKey('sortBy');
  }

  get priceFilterTitle(): string {
    return this.priceFilter?.name || 'Ваш бюджет';
  }

  get airlineValues(): AviaFilterOption[] {
    return this.getFilterValues(this.airlinesFilter);
  }

  get flightTypeValues(): AviaFilterOption[] {
    return this.getFilterValues(this.flightTypeFilter);
  }

  get sortByValues(): AviaFilterOption[] {
    return this.getFilterValues(this.sortByFilter);
  }

  get filterOptionValues(): AviaFilterOption[] {
    return (this.filter?.values || [])
      .map((value) => this.normalizeFilterOption(value))
      .filter((value) => value.filterValue);
  }

  get stopsFilter(): AviaSearchFilterItem | null {
    return this.getFilterByKey('stops');
  }

  get stopoverDurationFilter(): AviaSearchFilterItem | null {
    return this.getFilterByKey('stopoverDuration');
  }

  get withoutNightStopoversFilter(): AviaSearchFilterItem | null {
    return this.getFilterByKey('withoutNightStopovers');
  }

  get stopsValues(): AviaFilterOption[] {
    return this.getFilterValues(this.stopsFilter);
  }

  get stopoverDurationValues(): AviaFilterOption[] {
    return this.getFilterValues(this.stopoverDurationFilter);
  }

  get withoutNightStopoversTitle(): string {
    return this.withoutNightStopoversFilter?.name || 'Без ночных пересадок';
  }

  get departureTimeFromOriginFilter(): AviaSearchFilterItem | null {
    return this.getFilterByKey('departureTimeFromOrigin');
  }

  get departureTimeFromDestinationFilter(): AviaSearchFilterItem | null {
    return this.getFilterByKey('departureTimeFromDestination');
  }

  get departureTimeFromOriginTitle(): string {
    return this.departureTimeFromOriginFilter?.name || 'Вылет туда';
  }

  get departureTimeFromDestinationTitle(): string {
    return this.departureTimeFromDestinationFilter?.name || 'Вылет обратно';
  }

  get departureTimeFromOriginLabel(): string {
    return `${this.formatTime(this.departureTimeFromOriginValue)} - ${this.formatTime(this.departureTimeFromOriginHighValue)}`;
  }

  get departureTimeFromDestinationLabel(): string {
    return `${this.formatTime(this.departureTimeFromDestinationValue)} - ${this.formatTime(this.departureTimeFromDestinationHighValue)}`;
  }

  get isWithoutNightStopoversSelected(): boolean {
    return this.selectedWithoutNightStopovers === this.withoutNightStopoversValue;
  }

  get isAnyAirlineSelected(): boolean {
    return this.selectedAirlines.length === 0;
  }

  get isResetButtonShown(): boolean {

    if (this.isAllFiltersGroupFilter) {
      return this.isAllFiltersChanged;
    }

    if (this.isPriceFilter) {
      return this.isPriceChanged;
    }

    if (this.isAirlinesFilter) {
      return this.selectedAirlines.length > 0 || this.singleAirlineOnly || this.excludeLcc;
    }

    if (this.isFlightTypeFilter) {
      return this.selectedFlightType !== '';
    }

    if (this.isStopoversGroupFilter) {
      return this.isStopoversGroupChanged;
    }

    if (this.isDepartureTimeGroupFilter) {
      return this.isDepartureTimeGroupChanged;
    }

    if (this.isBaggageOnlyFilter) {
      return this.selectedBaggageOnly !== '';
    }

    if (this.isSortByFilter) {
      return this.selectedSortBy !== 'price';
    }

    return false;
  }

  changePriceRange(params: any) {
    this.isPriceManuallyChanged = true;
    this.minPriceValue = this.normalizePriceValue(params.value);
    this.maxPriceValue = this.normalizePriceValue(params.highValue);
    this.updatePriceInputValues();
  }

  inputMinPrice(event: Event) {
    this.isPriceManuallyChanged = true;
    const inputValue = this.formatPriceInput(event);
    this.minPriceInputValue = inputValue.formatted;

    if (inputValue.formatted === '') {
      return;
    }

    this.minPriceValue = Math.min(this.normalizePriceValue(inputValue.value), this.maxPriceValue);
  }

  inputMaxPrice(event: Event) {
    this.isPriceManuallyChanged = true;
    const inputValue = this.formatPriceInput(event);
    this.maxPriceInputValue = inputValue.formatted;

    if (inputValue.formatted === '') {
      return;
    }

    this.maxPriceValue = Math.max(this.normalizePriceValue(inputValue.value), this.minPriceValue);
  }

  changeMinPrice(event: Event) {
    this.isPriceManuallyChanged = true;
    const value = this.normalizePriceValue(this.parseInputValue(event));
    this.minPriceValue = Math.min(value, this.maxPriceValue);
    this.updatePriceInputValues();
  }

  changeMaxPrice(event: Event) {
    this.isPriceManuallyChanged = true;
    const value = this.normalizePriceValue(this.parseInputValue(event));
    this.maxPriceValue = Math.max(value, this.minPriceValue);
    this.updatePriceInputValues();
  }

  resetCurrentFilter() {

    if (
      !this.filter?.filterKey
      && !this.isStopoversGroupFilter
      && !this.isDepartureTimeGroupFilter
      && !this.isAllFiltersGroupFilter
    ) {
      return;
    }

    if (this.isAllFiltersGroupFilter) {
      this.resetAllFilters();
      this.resetFilterGroup.emit(this.allFilterKeys);
      return;
    }

    if (this.isPriceFilter) {
      this.minPriceValue = this.minPriceLimit;
      this.maxPriceValue = this.maxPriceLimit;
      this.isPriceManuallyChanged = false;
      this.isPriceInitiallyApplied = false;
      this.updatePriceInputValues();
    }

    if (this.isAirlinesFilter) {
      this.selectedAirlines = [];
      this.singleAirlineOnly = false;
      this.excludeLcc = false;
      this.resetFilterGroup.emit(['airlines', 'singleAirline', 'excludeLcc']);
      return;
    }

    if (this.isFlightTypeFilter) {
      this.selectedFlightType = '';
    }

    if (this.isStopoversGroupFilter) {
      this.selectedStops = '';
      this.selectedStopoverDuration = '';
      this.selectedWithoutNightStopovers = '';
      this.resetFilterGroup.emit(this.stopoverFilterKeys);
      return;
    }

    if (this.isDepartureTimeGroupFilter) {
      this.resetDepartureTimeValues();
      this.resetFilterGroup.emit(this.departureTimeFilterKeys);
      return;
    }

    if (this.isBaggageOnlyFilter) {
      this.selectedBaggageOnly = '';
    }

    if (this.isSortByFilter) {
      this.selectedSortBy = 'price';
    }

    this.resetFilter.emit(this.filter.filterKey);
  }

  applyCurrentFilter() {

    if (this.isAllFiltersGroupFilter) {
      this.applyFilterGroup.emit({
        filterKeys: this.allFilterKeys,
        filters: this.buildAppliedFiltersFromCurrentState(),
      });
      return;
    }

    if (this.isPriceFilter) {
      this.applyFilter.emit({
        filterKey: 'price',
        selected: {
          from: String(this.minPriceValue),
          to: String(this.maxPriceValue),
        },
      });
      return;
    }

    if (this.isAirlinesFilter) {
      this.applyFilterGroup.emit({
        filterKeys: ['airlines', 'singleAirline', 'excludeLcc'],
        filters: this.buildAirlineFilters(),
      });
      return;
    }

    if (this.isFlightTypeFilter) {
      if (!this.selectedFlightType) {
        this.resetFilter.emit('flightType');
        return;
      }

      this.applyFilter.emit({
        filterKey: 'flightType',
        selected: this.selectedFlightType,
      });
      return;
    }

    if (this.isStopoversGroupFilter) {
      const filters: AviaSearchAppliedFilter[] = [];

      if (this.selectedStops) {
        filters.push({
          filterKey: 'stops',
          selected: this.selectedStops,
        });
      }

      if (this.selectedStopoverDuration) {
        filters.push({
          filterKey: 'stopoverDuration',
          selected: this.selectedStopoverDuration,
        });
      }

      if (this.selectedWithoutNightStopovers) {
        filters.push({
          filterKey: 'withoutNightStopovers',
          selected: this.selectedWithoutNightStopovers,
        });
      }

      this.applyFilterGroup.emit({
        filterKeys: this.stopoverFilterKeys,
        filters,
      });
      return;
    }

    if (this.isDepartureTimeGroupFilter) {
      const filters: AviaSearchAppliedFilter[] = [];

      if (this.isTimeRangeChanged(
        this.departureTimeFromOriginValue,
        this.departureTimeFromOriginHighValue,
        this.departureTimeFromOriginMin,
        this.departureTimeFromOriginMax,
      )) {
        filters.push({
          filterKey: 'departureTimeFromOrigin',
          selected: {
            from: this.formatTime(this.departureTimeFromOriginValue),
            to: this.formatTime(this.departureTimeFromOriginHighValue),
          },
        });
      }

      if (this.isTimeRangeChanged(
        this.departureTimeFromDestinationValue,
        this.departureTimeFromDestinationHighValue,
        this.departureTimeFromDestinationMin,
        this.departureTimeFromDestinationMax,
      )) {
        filters.push({
          filterKey: 'departureTimeFromDestination',
          selected: {
            from: this.formatTime(this.departureTimeFromDestinationValue),
            to: this.formatTime(this.departureTimeFromDestinationHighValue),
          },
        });
      }

      this.applyFilterGroup.emit({
        filterKeys: this.departureTimeFilterKeys,
        filters,
      });
      return;
    }

    if (this.isBaggageOnlyFilter) {
      if (!this.selectedBaggageOnly) {
        this.resetFilter.emit('baggageOnly');
        return;
      }

      this.applyFilter.emit({
        filterKey: 'baggageOnly',
        selected: this.selectedBaggageOnly,
      });
      return;
    }

    if (this.isSortByFilter) {
      if (this.selectedSortBy === 'price') {
        this.resetFilter.emit('sortBy');
        return;
      }

      this.applyFilter.emit({
        filterKey: 'sortBy',
        selected: this.selectedSortBy,
      });
    }
  }

  selectAnyAirline() {
    this.singleAirlineOnly = false;
    this.selectedAirlines = [];
  }

  toggleAirline(airlineCode: string) {
    if (this.selectedAirlines.includes(airlineCode)) {
      this.selectedAirlines = this.selectedAirlines.filter((code) => code !== airlineCode);
      if (this.isAnyAirlineSelected) {
        this.singleAirlineOnly = false;
      }
      return;
    }

    this.selectedAirlines = [...this.selectedAirlines, airlineCode];
  }

  isAirlineSelected(airlineCode: string): boolean {
    return this.selectedAirlines.includes(airlineCode);
  }

  selectFlightType(flightType: AviaFilterOption) {
    this.selectedFlightType = flightType.isNoop
      ? ''
      : flightType.filterValue;
  }

  isFlightTypeSelected(flightType: AviaFilterOption): boolean {
    if (flightType.isNoop) {
      return this.selectedFlightType === '';
    }

    return this.selectedFlightType === flightType.filterValue;
  }

  selectSortBy(sortBy: AviaFilterOption) {
    this.selectedSortBy = sortBy.isNoop
      ? 'price'
      : sortBy.filterValue;
  }

  isSortBySelected(sortBy: AviaFilterOption): boolean {
    if (sortBy.isNoop) {
      return this.selectedSortBy === 'price';
    }

    return this.selectedSortBy === sortBy.filterValue;
  }

  selectStops(stops: AviaFilterOption) {
    this.selectedStops = stops.isNoop
      ? ''
      : stops.filterValue;
  }

  isStopsSelected(stops: AviaFilterOption): boolean {
    if (stops.isNoop) {
      return this.selectedStops === '';
    }

    return this.selectedStops === stops.filterValue;
  }

  selectStopoverDuration(stopoverDuration: AviaFilterOption) {
    this.selectedStopoverDuration = stopoverDuration.isNoop
      ? ''
      : stopoverDuration.filterValue;
  }

  isStopoverDurationSelected(stopoverDuration: AviaFilterOption): boolean {
    if (stopoverDuration.isNoop) {
      return this.selectedStopoverDuration === '';
    }

    return this.selectedStopoverDuration === stopoverDuration.filterValue;
  }

  toggleWithoutNightStopovers() {
    this.selectedWithoutNightStopovers = this.isWithoutNightStopoversSelected
      ? ''
      : this.withoutNightStopoversValue;
  }

  selectBaggageOnly(value: string) {
    this.selectedBaggageOnly = value === 'true'
      ? 'true'
      : '';
  }

  isBaggageOnlySelected(value: string): boolean {
    if (value === 'false') {
      return this.selectedBaggageOnly === '';
    }

    return this.selectedBaggageOnly === value;
  }

  changeDepartureTimeFromOriginRange(params: any) {
    this.departureTimeFromOriginValue = this.normalizeTimeValue(
      params.value,
      this.departureTimeFromOriginMin,
      this.departureTimeFromOriginMax,
    );
    this.departureTimeFromOriginHighValue = this.normalizeTimeValue(
      params.highValue,
      this.departureTimeFromOriginMin,
      this.departureTimeFromOriginMax,
    );
  }

  changeDepartureTimeFromDestinationRange(params: any) {
    this.departureTimeFromDestinationValue = this.normalizeTimeValue(
      params.value,
      this.departureTimeFromDestinationMin,
      this.departureTimeFromDestinationMax,
    );
    this.departureTimeFromDestinationHighValue = this.normalizeTimeValue(
      params.highValue,
      this.departureTimeFromDestinationMin,
      this.departureTimeFromDestinationMax,
    );
  }

  getAirlineLogoSrc(airlineCode: string): string {
    return `/x-assets/img/airlines/${airlineCode.toUpperCase()}.png`;
  }

  setAirlineLogoFallback(airlineCode: string) {
    this.noLogoAirlines = new Set([
      ...this.noLogoAirlines,
      airlineCode,
    ]);
  }

  isAirlineLogoFallback(airlineCode: string): boolean {
    return this.noLogoAirlines.has(airlineCode);
  }

  private initializeCurrentFilter() {

    if (this.isAllFiltersGroupFilter) {
      this.initializeAllFilters();
      return;
    }

    if (this.isPriceFilter) {
      this.initializePriceFilter();
    }

    if (this.isAirlinesFilter) {
      this.initializeAirlinesFilter();
    }

    if (this.isFlightTypeFilter) {
      this.initializeFlightTypeFilter();
    }

    if (this.isStopoversGroupFilter) {
      this.initializeStopoversGroupFilter();
    }

    if (this.isBaggageOnlyFilter) {
      this.initializeBaggageOnlyFilter();
    }

    if (this.isDepartureTimeGroupFilter) {
      this.initializeDepartureTimeGroupFilter();
    }

    if (this.isSortByFilter) {
      this.initializeSortByFilter();
    }
  }

  private initializePriceFilter() {
    const priceFilter = this.priceFilter;
    const appliedFilter = this.getAppliedFilterByKey('price');
    this.isPriceManuallyChanged = false;
    this.isPriceInitiallyApplied = Boolean(appliedFilter);

    this.minPriceLimit = this.getPriceAmount(priceFilter?.min);
    this.maxPriceLimit = this.getPriceAmount(priceFilter?.max);
    this.priceCurrencySign = this.getPriceCurrencySign(priceFilter?.min);

    const step = Number(priceFilter?.step) || this.getDefaultPriceStep(priceFilter?.min?.currency);
    this.priceOptions = {
      floor: this.minPriceLimit,
      ceil: this.maxPriceLimit,
      step,
      translate: (): string => '',
    };

    const selected = this.getPriceSelected(appliedFilter?.selected || priceFilter?.selected);
    this.minPriceValue = selected?.from
      ? this.normalizePriceValue(Number(selected.from))
      : this.minPriceLimit;
    this.maxPriceValue = selected?.to
      ? this.normalizePriceValue(Number(selected.to))
      : this.maxPriceLimit;
    this.updatePriceInputValues();
  }

  private buildAirlineFilters(): AviaSearchAppliedFilter[] {
    const filters: AviaSearchAppliedFilter[] = [];
    if (this.selectedAirlines.length) {
      filters.push({ filterKey: 'airlines', selected: [...this.selectedAirlines] });
    }
    if (this.singleAirlineOnly && !this.isAnyAirlineSelected) {
      filters.push({ filterKey: 'singleAirline', selected: 'true' });
    }
    if (this.excludeLcc) {
      filters.push({ filterKey: 'excludeLcc', selected: 'true' });
    }
    return filters;
  }

  private initializeAirlinesFilter() {
    this.excludeLcc = this.getAppliedScalarValue('excludeLcc', this.excludeLccFilter) === 'true';
    const appliedFilter = this.getAppliedFilterByKey('airlines');
    const selected = appliedFilter?.selected || this.airlinesFilter?.selected;
    this.selectedAirlines = Array.isArray(selected)
      ? [...selected]
      : [];
    this.singleAirlineOnly = !this.isAnyAirlineSelected
      && this.getAppliedScalarValue('singleAirline', this.singleAirlineFilter) === 'true';
  }

  private initializeFlightTypeFilter() {
    this.selectedFlightType = this.getAppliedScalarValue('flightType', this.flightTypeFilter);
  }

  private initializeStopoversGroupFilter() {
    this.selectedStops = this.getAppliedScalarValue('stops');
    this.selectedStopoverDuration = this.getAppliedScalarValue('stopoverDuration');
    this.selectedWithoutNightStopovers = this.getAppliedScalarValue('withoutNightStopovers');
  }

  private initializeBaggageOnlyFilter() {
    this.selectedBaggageOnly = this.getAppliedScalarValue('baggageOnly', this.baggageOnlyFilter);
  }

  private initializeSortByFilter() {
    const appliedFilter = this.getAppliedFilterByKey('sortBy');
    const selected = appliedFilter?.selected || this.sortByFilter?.selected;
    this.selectedSortBy = typeof selected === 'string'
      ? selected
      : 'price';
  }

  private initializeDepartureTimeGroupFilter() {
    this.initializeDepartureTimeRange('departureTimeFromOrigin');
    this.initializeDepartureTimeRange('departureTimeFromDestination');
  }

  private initializeAllFilters() {
    this.initializePriceFilter();
    this.initializeAirlinesFilter();
    this.initializeFlightTypeFilter();
    this.initializeStopoversGroupFilter();
    this.initializeBaggageOnlyFilter();
    this.initializeDepartureTimeGroupFilter();
  }

  private resetAllFilters() {
    this.excludeLcc = false;
    this.singleAirlineOnly = false;
    this.minPriceValue = this.minPriceLimit;
    this.maxPriceValue = this.maxPriceLimit;
    this.isPriceManuallyChanged = false;
    this.isPriceInitiallyApplied = false;
    this.updatePriceInputValues();
    this.selectedAirlines = [];
    this.selectedFlightType = '';
    this.selectedStops = '';
    this.selectedStopoverDuration = '';
    this.selectedWithoutNightStopovers = '';
    this.selectedBaggageOnly = '';
    this.resetDepartureTimeValues();
  }

  private buildAppliedFiltersFromCurrentState(): AviaSearchAppliedFilter[] {
    const filters: AviaSearchAppliedFilter[] = [];

    if (this.isPriceChanged) {
      filters.push({
        filterKey: 'price',
        selected: {
          from: String(this.minPriceValue),
          to: String(this.maxPriceValue),
        },
      });
    }

    filters.push(...this.buildAirlineFilters());

    if (this.selectedFlightType) {
      filters.push({
        filterKey: 'flightType',
        selected: this.selectedFlightType,
      });
    }

    if (this.selectedStops) {
      filters.push({
        filterKey: 'stops',
        selected: this.selectedStops,
      });
    }

    if (this.selectedStopoverDuration) {
      filters.push({
        filterKey: 'stopoverDuration',
        selected: this.selectedStopoverDuration,
      });
    }

    if (this.selectedWithoutNightStopovers) {
      filters.push({
        filterKey: 'withoutNightStopovers',
        selected: this.selectedWithoutNightStopovers,
      });
    }

    if (this.selectedBaggageOnly) {
      filters.push({
        filterKey: 'baggageOnly',
        selected: this.selectedBaggageOnly,
      });
    }

    if (this.isTimeRangeChanged(
      this.departureTimeFromOriginValue,
      this.departureTimeFromOriginHighValue,
      this.departureTimeFromOriginMin,
      this.departureTimeFromOriginMax,
    )) {
      filters.push({
        filterKey: 'departureTimeFromOrigin',
        selected: {
          from: this.formatTime(this.departureTimeFromOriginValue),
          to: this.formatTime(this.departureTimeFromOriginHighValue),
        },
      });
    }

    if (this.isTimeRangeChanged(
      this.departureTimeFromDestinationValue,
      this.departureTimeFromDestinationHighValue,
      this.departureTimeFromDestinationMin,
      this.departureTimeFromDestinationMax,
    )) {
      filters.push({
        filterKey: 'departureTimeFromDestination',
        selected: {
          from: this.formatTime(this.departureTimeFromDestinationValue),
          to: this.formatTime(this.departureTimeFromDestinationHighValue),
        },
      });
    }

    return filters;
  }

  private get isPriceChanged(): boolean {
    if (!this.isPriceManuallyChanged && !this.isPriceInitiallyApplied) {
      return false;
    }

    return this.minPriceValue !== this.minPriceLimit
      || this.maxPriceValue !== this.maxPriceLimit;
  }

  private get isStopoversGroupChanged(): boolean {
    return Boolean(
      this.selectedStops
      || this.selectedStopoverDuration
      || this.selectedWithoutNightStopovers,
    );
  }

  private get isDepartureTimeGroupChanged(): boolean {
    return this.isTimeRangeChanged(
      this.departureTimeFromOriginValue,
      this.departureTimeFromOriginHighValue,
      this.departureTimeFromOriginMin,
      this.departureTimeFromOriginMax,
    )
      || this.isTimeRangeChanged(
        this.departureTimeFromDestinationValue,
        this.departureTimeFromDestinationHighValue,
        this.departureTimeFromDestinationMin,
        this.departureTimeFromDestinationMax,
      );
  }

  private get isAllFiltersChanged(): boolean {
    return Boolean(
      this.isPriceChanged
      || this.selectedAirlines.length
      || this.singleAirlineOnly
      || this.excludeLcc
      || this.selectedFlightType
      || this.isStopoversGroupChanged
      || this.selectedBaggageOnly
      || this.isDepartureTimeGroupChanged,
    );
  }

  private getAppliedFilterByKey(filterKey: string): AviaSearchAppliedFilter | null {
    if (this.appliedFilter?.filterKey === filterKey) {
      return this.appliedFilter;
    }

    return this.appliedFilters.find((filter) => filter.filterKey === filterKey) || null;
  }

  private getPriceSelected(selected: AviaSearchFilterSelected | undefined): AviaSearchPriceSelected | null {
    if (!selected || typeof selected !== 'object' || Array.isArray(selected)) {
      return null;
    }

    return selected;
  }

  private getPriceAmount(price: AviaSearchFilterPrice | undefined): number {
    return Number(price?.amount ?? price?.value ?? 0);
  }

  private getPriceCurrencySign(price: AviaSearchFilterPrice | undefined): string {
    return price?.currencySign || price?.currency_sign || price?.currency || '';
  }

  private getDefaultPriceStep(currency: string | undefined): number {
    switch (currency) {
      case 'usd':
      case 'eur':
        return 10;
      case 'kzt':
      default:
        return 10000;
    }
  }

  private normalizePriceValue(value: number): number {
    if (!Number.isFinite(value)) {
      return this.minPriceLimit;
    }

    return Math.min(Math.max(value, this.minPriceLimit), this.maxPriceLimit);
  }

  private parseInputValue(event: Event): number {
    const input = event.target as HTMLInputElement;

    return Number(input.value.replace(/\D/g, ''));
  }

  private formatPriceInput(event: Event): { value: number; formatted: string } {
    const input = event.target as HTMLInputElement;
    const digitsCountBeforeCaret = this.getDigitsCountBeforeCaret(input);
    const digits = input.value.replace(/\D/g, '');
    const value = Number(digits);
    const formatted = digits ? this.formatPrice(value) : '';

    input.value = formatted;
    this.setInputCaretByDigitsCount(input, digitsCountBeforeCaret);

    return {value, formatted};
  }

  private getDigitsCountBeforeCaret(input: HTMLInputElement): number {
    const selectionStart = input.selectionStart ?? input.value.length;
    const valueBeforeCaret = input.value.slice(0, selectionStart);

    return valueBeforeCaret.replace(/\D/g, '').length;
  }

  private setInputCaretByDigitsCount(input: HTMLInputElement, digitsCount: number) {
    if (digitsCount <= 0) {
      input.setSelectionRange(0, 0);
      return;
    }

    let currentDigitsCount = 0;
    let caretPosition = input.value.length;

    for (let index = 0; index < input.value.length; index++) {
      if (/\d/.test(input.value[index])) {
        currentDigitsCount++;
      }

      if (currentDigitsCount >= digitsCount) {
        caretPosition = index + 1;
        break;
      }
    }

    input.setSelectionRange(caretPosition, caretPosition);
  }

  private updatePriceInputValues() {
    this.minPriceInputValue = this.formatPrice(this.minPriceValue);
    this.maxPriceInputValue = this.formatPrice(this.maxPriceValue);
  }

  private formatPrice(value: number): string {
    const formattedValue = String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

    return this.priceCurrencySign ? `${formattedValue} ${this.priceCurrencySign}` : formattedValue;
  }

  private getFilterByKey(filterKey: string): AviaSearchFilterItem | null {
    return this.filters.find((filter) => filter.filterKey === filterKey) || null;
  }

  private getFilterValues(filter: AviaSearchFilterItem | null): AviaFilterOption[] {
    return (filter?.values || [])
      .map((value) => this.normalizeFilterOption(value))
      .filter((value) => value.filterValue);
  }

  private getAppliedScalarValue(filterKey: string, fallbackFilter: AviaSearchFilterItem | null = null): string {
    const appliedFilter = this.appliedFilters.find((filter) => filter.filterKey === filterKey);
    const filter = this.getFilterByKey(filterKey) || fallbackFilter;
    const selected = appliedFilter?.selected || filter?.selected;

    if (typeof selected !== 'string') {
      return '';
    }

    const selectedOption = this.getFilterValues(filter).find((option) => option.filterValue === selected);
    if (
      selectedOption?.isNoop
      || (['withoutNightStopovers', 'baggageOnly'].includes(filterKey) && selected === 'false')
    ) {
      return '';
    }

    return selected;
  }

  private get withoutNightStopoversValue(): string {
    const selectedValue = this.getAppliedScalarValue('withoutNightStopovers');
    const values = this.getFilterValues(this.withoutNightStopoversFilter);
    const value = values.find((item) => !item.isNoop)?.filterValue || selectedValue;

    return value || 'true';
  }

  private initializeDepartureTimeRange(filterKey: string) {
    const filter = this.getFilterByKey(filterKey);
    const min = this.getTimeFilterLimit(filter, 'min', 0);
    const max = this.getTimeFilterLimit(filter, 'max', 1439);
    const selected = this.getAppliedTimeValue(filterKey, filter, min, max);
    const options = this.createTimeOptions(min, max, Number(filter?.step) || 15);

    if (filterKey === 'departureTimeFromOrigin') {
      this.departureTimeFromOriginMin = min;
      this.departureTimeFromOriginMax = max;
      this.departureTimeFromOriginValue = selected.from;
      this.departureTimeFromOriginHighValue = selected.to;
      this.departureTimeFromOriginOptions = options;
      return;
    }

    this.departureTimeFromDestinationMin = min;
    this.departureTimeFromDestinationMax = max;
    this.departureTimeFromDestinationValue = selected.from;
    this.departureTimeFromDestinationHighValue = selected.to;
    this.departureTimeFromDestinationOptions = options;
  }

  private resetDepartureTimeValues() {
    this.departureTimeFromOriginValue = this.departureTimeFromOriginMin;
    this.departureTimeFromOriginHighValue = this.departureTimeFromOriginMax;
    this.departureTimeFromDestinationValue = this.departureTimeFromDestinationMin;
    this.departureTimeFromDestinationHighValue = this.departureTimeFromDestinationMax;
  }

  private getAppliedTimeValue(
    filterKey: string,
    filter: AviaSearchFilterItem | null,
    min: number,
    max: number,
  ): { from: number; to: number } {
    const appliedFilter = this.appliedFilters.find((item) => item.filterKey === filterKey);
    const selected = appliedFilter?.selected || filter?.selected;

    if (!selected || typeof selected !== 'object' || Array.isArray(selected)) {
      return {from: min, to: max};
    }

    const range = selected as AviaSearchTimeSelected;
    return {
      from: this.normalizeTimeValue(this.parseTime(range.from, min), min, max),
      to: this.normalizeTimeValue(this.parseTime(range.to, max), min, max),
    };
  }

  private getTimeFilterLimit(
    filter: AviaSearchFilterItem | null,
    limitKey: 'min' | 'max',
    defaultValue: number,
  ): number {
    const value = (filter as any)?.[limitKey];

    if (typeof value === 'string') {
      return this.parseTime(value, defaultValue);
    }

    if (typeof value === 'number') {
      return this.normalizeTimeValue(value, 0, 1439);
    }

    if (value && typeof value === 'object') {
      const objectValue = value.value ?? value.amount ?? value.time;
      if (typeof objectValue === 'string') {
        return this.parseTime(objectValue, defaultValue);
      }

      if (typeof objectValue === 'number') {
        return this.normalizeTimeValue(objectValue, 0, 1439);
      }
    }

    return defaultValue;
  }

  private createTimeOptions(floor = 0, ceil = 1439, step = 15): Options {
    return {
      floor,
      ceil,
      step,
      translate: (value: number): string => this.formatTime(value),
    };
  }

  private isTimeRangeChanged(value: number, highValue: number, min: number, max: number): boolean {
    return value !== min || highValue !== max;
  }

  private normalizeTimeValue(value: number, min: number, max: number): number {
    if (!Number.isFinite(value)) {
      return min;
    }

    return Math.min(Math.max(Math.round(value), min), max);
  }

  private parseTime(value: string | undefined, fallback: number): number {
    if (!value) {
      return fallback;
    }

    const [hours, minutes] = value.split(':').map((item) => Number(item));
    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return fallback;
    }

    return this.normalizeTimeValue(hours * 60 + minutes, 0, 1439);
  }

  private formatTime(value: number): string {
    const normalizedValue = this.normalizeTimeValue(value, 0, 1439);
    const hours = Math.floor(normalizedValue / 60);
    const minutes = normalizedValue % 60;

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }

  private normalizeFilterOption(value: AviaSearchFilterValue | string): AviaFilterOption {
    if (typeof value === 'string') {
      return {
        filterValue: value,
        viewValue: value,
        isNoop: false,
      };
    }

    return {
      filterValue: value.filterValue,
      viewValue: value.viewValue || value.filterValue,
      isNoop: Boolean(value.isNoop),
    };
  }
}
